import { useEffect, useRef } from 'react';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { useAppSelector } from '../../hooks/useAppSelector';
import { selectAuthBootstrapComplete } from '../../store/features/auth/authSelectors';
import { fetchCartSummary } from '../../store/cartSlice';

function scheduleIdleTask(task: () => void): () => void {
  if (typeof globalThis.window === 'undefined') {
    return () => undefined;
  }

  const win = globalThis.window as Window & {
    requestIdleCallback?: (
      cb: () => void,
      opts?: { timeout?: number },
    ) => number;
    cancelIdleCallback?: (id: number) => void;
  };

  if (typeof win.requestIdleCallback === 'function') {
    const id = win.requestIdleCallback(task, { timeout: 2500 });
    return () => win.cancelIdleCallback?.(id);
  }

  const timer = globalThis.setTimeout(task, 900);
  return () => globalThis.clearTimeout(timer);
}

/**
 * Refreshes lightweight cart summary after public cold load (badge only).
 * Does not hydrate full cart line items.
 */
export function CartBadgeRefresh() {
  const dispatch = useAppDispatch();
  const bootstrapComplete = useAppSelector(selectAuthBootstrapComplete);
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const sessionId = useAppSelector((state) => state.cart.sessionId);
  const lastAuthRef = useRef(isAuthenticated);

  useEffect(() => {
    if (!bootstrapComplete) return;

    const authChanged = lastAuthRef.current !== isAuthenticated;
    lastAuthRef.current = isAuthenticated;

    let cancelled = false;
    const cancelSchedule = scheduleIdleTask(() => {
      if (!cancelled) {
        void dispatch(fetchCartSummary({ force: authChanged }));
      }
    });

    return () => {
      cancelled = true;
      cancelSchedule();
    };
  }, [bootstrapComplete, dispatch, isAuthenticated, sessionId]);

  return null;
}

export default CartBadgeRefresh;
